/**
 * commonApis.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * All HTTP fetch wrappers used across the LM frontend.
 *
 * Key changes from the old version:
 *  - fetch3/4/5 now CHECK res.ok and throw an ApiError if the server responds
 *    with a non-2xx status. This ensures React Query's onError is always called
 *    with a proper Error object, not undefined.
 *  - A shared `safeFetch` helper deduplicates the try/catch + error throw.
 *  - fetch2/6/7/8 (multipart) follow the same pattern.
 */

import { createApiError } from "../utilities/apiErrorHandler";

const appendAuthType = (url: string) => {
  const authParam = "authType=client";
  if (url.includes("?")) {
    return url.includes("authType=") ? url : `${url}&${authParam}`;
  }
  return `${url}?${authParam}`;
};

async function safeFetch(input: RequestInfo, init: RequestInit): Promise<any> {
  const urlWithAuth = typeof input === "string" ? appendAuthType(input) : input;
  const res = await fetch(urlWithAuth, init);
  const json = await res.json();

  // If the server returned a non-2xx status OR success:false, create a proper Error
  if (json?.message === 'Invalid or expired token') {
    return json
  }
  if (!res.ok || json?.success === false) {
    throw createApiError(json);
  }

  return json;
}

// ─── fetch3: GET / generic typed requests (no body) ──────────────────────────

export const fetch3 = async (api: string, type: string): Promise<any> => {
  return safeFetch(api, {
    method: type,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    mode: "cors",
    credentials: "include",
  });
};

// ─── fetch4: POST with JSON body ──────────────────────────────────────────────

export const fetch4 = async (api: string, body: any = {}): Promise<any> => {
  return safeFetch(api, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    mode: "cors",
    credentials: "include",
    body: JSON.stringify(body),
  });
};

// ─── fetch5: PATCH / PUT / DELETE with JSON body ─────────────────────────────

export const fetch5 = async (
  api: string,
  type: string,
  body?: any
): Promise<any> => {
  const init: RequestInit = {
    method: type,
    headers: {
      "content-type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  };

  if (body !== undefined && body !== null) {
    init.body = JSON.stringify(body);
  }

  return safeFetch(api, init);
};

// ─── fetch2: POST with FormData ───────────────────────────────────────────────

export const fetch2 = async (api: string, body: any): Promise<any> => {
  const data = new FormData();

  if (body) {
    for (const key of Object.keys(body)) {
      const value = body[key];
      if (Array.isArray(value)) {
        for (const item of value) data.append(key, item);
      } else {
        data.append(key, value);
      }
    }
  }

  return safeFetch(api, {
    method: "POST",
    credentials: "include",
    body: data,
  });
};

// ─── fetch6: PATCH with FormData ─────────────────────────────────────────────

export const fetch6 = async (api: string, body: any): Promise<any> => {
  const data = new FormData();

  if (body) {
    for (const key of Object.keys(body)) {
      const value = body[key];
      if (Array.isArray(value)) {
        for (const item of value) data.append(key, item);
      } else {
        data.append(key, value);
      }
    }
  }

  return safeFetch(api, {
    method: "PATCH",
    credentials: "include",
    body: data,
  });
};

// ─── fetch7: Typed method + FormData ─────────────────────────────────────────

export const fetch7 = async (api: string, type: string, body: any): Promise<any> => {
  const data = new FormData();

  if (body) {
    for (const key of Object.keys(body)) {
      const value = body[key];
      if (Array.isArray(value)) {
        for (const item of value) data.append(key, item);
      } else {
        data.append(key, value);
      }
    }
  }

  return safeFetch(api, {
    method: type,
    credentials: "include",
    body: data,
  });
};

// ─── fetch8: POST/PATCH with mixed FormData (files + JSON variants) ───────────

export const fetch8 = async (
  api: string,
  method: string = "POST",
  body?: Record<string, any>
): Promise<any> => {
  const init: RequestInit = { method, credentials: "include" };

  if (body) {
    const data = new FormData();

    for (const key of Object.keys(body)) {
      const value = body[key];
      if (value === undefined || value === null) continue;

      if (key === "variants" && Array.isArray(value)) {
        value.forEach((variant, index) => {
          const variantCopy = { ...variant };
          if (variantCopy.image instanceof File || variantCopy.image instanceof Blob) {
            data.append(`variant_image_${index}`, variantCopy.image);
            variantCopy.image = `PENDING_FILE_${index}`;
          }
          data.append("variants", JSON.stringify(variantCopy));
        });
      } else if (Array.isArray(value)) {
        for (const item of value) {
          if (item instanceof File || item instanceof Blob) {
            data.append(key, item);
          } else {
            data.append(key, typeof item === "object" ? JSON.stringify(item) : item);
          }
        }
      } else if (value instanceof File || value instanceof Blob) {
        data.append(key, value);
      } else if (typeof value === "object") {
        data.append(key, JSON.stringify(value));
      } else {
        data.append(key, value);
      }
    }

    init.body = data;
  }

  return safeFetch(api, init);
};
